import { Lang, Local, getLang } from '@/i18n/init';
import { user } from '@/model/CustomerDetail';

/**
 * 商品搜索 语言枚举
 */
export enum SearchLangType {
    /** 日文 */
    JA = '1',
    /** 中文 */
    ZH = '2',
    /** 韩文 */
    KR = '3',
    /** 英文 */
    EN = '4'
}

/**
 * 国家及货币单位
 */
enum Site {
    /**
     * 日本站
     */
    JP = 'JapanStation',
    /**
     * 韩国站
     */
    KR = 'KoreaStation',
    /**
     * 英国站
     */
    UK = 'UkStation'
}

export const SiteCodeMap = new Map([
    ['JapanStation', 'JP'],
    ['KoreaStation', 'KR'],
    ['UkStation', 'GB']
]);

export const CodeToSiteMap = new Map([
    ['JP', 'JapanStation'],
    ['KR', 'KoreaStation'],
    ['GB', 'UkStation']
]);

enum simpleSite {
    /**
     * 日本站
     */
    JP = 'ja',
    /**
     * 韩国站
     */
    KR = 'ko',
    /**
     * 英国站
     */
    UK = 'en'
}
/**
 * 站点对应的货币单位
 */
const siteMapMonetaryUnit = new Map<string, string>();
siteMapMonetaryUnit.set(Site.JP, '円');
siteMapMonetaryUnit.set(Site.KR, '원');
siteMapMonetaryUnit.set(Site.UK, '$');

/**
 * 站点的导航条时区名字
 */
const siteMapAreaName = new Map<string, string>();
// 日本
siteMapAreaName.set(Site.JP, 'Tokyo');
// 韩国
siteMapAreaName.set(Site.KR, 'Seoul');
// GMT +n
siteMapAreaName.set(Site.UK, 'GMT');

/**
 * 国家枚举
 */
enum CountriesEnum {
    /**
     * 日本
     */
    Japan = 'JP',
    /**
     * 澳大利亚
     */
    Australia = 'AU',
    /**
     * 加拿大
     */
    Canada = 'CA',
    /**
     * 爱尔兰
     */
    Ireland = 'IE',

    /**
     * 韩国
     */
    Korea = 'KR',
    /**
     * 马来西亚
     */
    Malaysia = 'MY',
    /**
     * 新西兰
     */
    NewZealand = 'NZ',
    /**
     * 新加坡
     */
    Singapore = 'SG',
    /**
     * 英国
     */
    UK = 'GB',
    /**
     * 其他国家
     */
    other = 'OTHER'
}

/**
 * 国家对应的一些信息
 */
const countries = [
    {
        key: CountriesEnum.Japan,
        desc: '日本',
        navigatorLang: ['ja', 'ja_JP', 'ja-JP'],
        mobileAreaCode: '+81'
    },
    {
        key: CountriesEnum.Korea,
        desc: '韩国',
        navigatorLang: ['ko', 'ko_KR', 'ko-KR'],
        mobileAreaCode: '+82'
    },
    {
        key: CountriesEnum.Australia,
        desc: '澳大利亚',
        navigatorLang: ['en-AU', 'en'],
        mobileAreaCode: '+61'
    },
    {
        key: CountriesEnum.Canada,
        desc: '加拿大',
        navigatorLang: ['en-CA', 'fr-CA'],
        mobileAreaCode: '+1'
    },
    {
        key: CountriesEnum.Ireland,
        desc: '爱尔兰',
        navigatorLang: ['en-IE', 'en'],
        mobileAreaCode: '+353'
    },

    {
        key: CountriesEnum.Malaysia,
        desc: '马来西亚',
        navigatorLang: ['en-MY', 'en', 'ms-MY'],
        mobileAreaCode: '+60'
    },
    {
        key: CountriesEnum.NewZealand,
        desc: '新西兰',
        navigatorLang: ['en-NZ', 'en', 'zh-SG'],
        mobileAreaCode: '+64'
    },
    {
        key: CountriesEnum.Singapore,
        desc: '新加坡',
        navigatorLang: ['en-SG', 'zh-SG', 'en', 'zh'],
        mobileAreaCode: '+65'
    },
    {
        key: CountriesEnum.UK,
        desc: '英国',
        navigatorLang: ['en', 'en-GB', 'en_GB'],
        mobileAreaCode: '+44'
    },
    {
        key: CountriesEnum.other,
        desc: '其他国家和地区',
        navigatorLang: ['*'],
        mobileAreaCode: ''
    }
];

/**
 * 日本站点下的国家
 */
const japanStationCor2Country: string[] = [CountriesEnum.Japan];
/**
 * 韩国站点下的国家
 */
const koreaStationCor2Country: string[] = [CountriesEnum.Korea];
/**
 * 英国站点下的国家
 */
const ukStationCor2Country: string[] = [
    CountriesEnum.Australia,
    CountriesEnum.Canada,
    CountriesEnum.Ireland,
    CountriesEnum.Malaysia,
    CountriesEnum.NewZealand,
    CountriesEnum.Singapore,
    CountriesEnum.UK,
    CountriesEnum.other
];

// 国家货币
const countryCurrency = new Map<string, string>();
countryCurrency.set(Site.JP, 'JPY');
countryCurrency.set(Site.KR, 'KRW');
countryCurrency.set(Site.UK, 'USD');

/**
 * 未登录时，根据浏览器语言，返回用户所在站点
 * @returns Site
 */
const getCountryByNavigatorLang = (): Site => {
    const lang = getLang();
    const existCountry = countries.find((item) =>
        item.navigatorLang.includes(lang)
    );
    if (existCountry) {
        const countryKey = existCountry.key;
        const site = CodeToSiteMap.get(countryKey) as Site;
        return site;
    }
    return Site.JP;
};

enum TermType {
    /** 利用规约 */
    utilize = 'utilize',
    /** 隐私权限 */
    privacy = 'privacy',
    /** 次转正提醒 */
    regular = 'regular',
    /** OEM条款 */
    oem = 'oem'
}

/**
 * 获取语言选项
 */
const getSearchLangType = (lang: Lang) => {
    return {
        // 日语
        [Lang.ja_JP]: SearchLangType.JA,
        // 韩语
        [Lang.ko_KR]: SearchLangType.KR,
        // 英语
        [Lang.en_GB]: SearchLangType.EN,
        // 中文
        [Lang.zh_CN]: SearchLangType.ZH
    }[lang];
};

export {
    getSearchLangType,
    TermType,
    Site,
    siteMapMonetaryUnit,
    countries,
    CountriesEnum,
    countryCurrency,
    japanStationCor2Country,
    koreaStationCor2Country,
    ukStationCor2Country,
    siteMapAreaName,
    getCountryByNavigatorLang,
    simpleSite
};
